import { LoggerEventNames } from '@/lib/consts/logger';
import { SESSION_STORAGE_KEYS } from '@/lib/consts/sessionStorage';

/**
 * Carries an event across a hard navigation so the next document reports it.
 *
 * Some failures are recorded a line before the document is destroyed, and there
 * are two reasons the event would otherwise never leave the browser: RUM is
 * initialized after the main content paints, so on /login the event is usually
 * still sitting in the
 * in-memory queue; and the RUM SDK batches over the network with no unload
 * flush, so even an initialized send may not have been transmitted. A
 * synchronous sessionStorage write is the only step that survives both.
 *
 * sessionStorage, matching the pending identity: this belongs to one tab's
 * journey, and the TTL keeps an abandoned journey from resurfacing later.
 */
export enum RumEventLevel {
    ERROR = 'error',
    INFO = 'info',
}

export interface CarriedRumEvent {
    level: RumEventLevel;
    name: LoggerEventNames;
    data: Record<string, unknown>;
}

interface StoredRumEvent extends CarriedRumEvent {
    storedAt: number;
}

// A journey makes at most a couple of these; the cap only bounds a pathological
// redirect loop from filling the quota.
const MAX_CARRIED_EVENTS = 10;
const CARRIED_EVENT_TTL_MS = 5 * 60 * 1000;

export const carryRumEventToNextDocument = (event: CarriedRumEvent): void => {
    try {
        const stored: StoredRumEvent = { ...event, storedAt: Date.now() };
        const pending = [...readStoredEvents(), stored].slice(-MAX_CARRIED_EVENTS);
        sessionStorage.setItem(SESSION_STORAGE_KEYS.PENDING_RUM_EVENTS, JSON.stringify(pending));
    } catch {
        // Private mode / quota. Nothing else can outlive the navigation, so the
        // event is simply lost rather than breaking the navigation itself.
    }
};

/** Read once and clear, so a replayed event is never reported twice. */
export const drainCarriedRumEvents = (): CarriedRumEvent[] => {
    const stored = readStoredEvents();
    if (stored.length > 0) {
        clearCarriedRumEvents();
    }
    return stored
        .filter((event) => isFresh(event.storedAt))
        .map(({ level, name, data }) => ({ level, name, data }));
};

export const clearCarriedRumEvents = (): void => {
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.PENDING_RUM_EVENTS);
    } catch {
        // the TTL already bounds a value that cannot be removed
    }
};

const readStoredEvents = (): StoredRumEvent[] => {
    try {
        const raw = sessionStorage.getItem(SESSION_STORAGE_KEYS.PENDING_RUM_EVENTS);
        if (!raw) {
            return [];
        }
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(isStoredRumEvent) : [];
    } catch {
        return [];
    }
};

const isStoredRumEvent = (value: unknown): value is StoredRumEvent => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Partial<StoredRumEvent>;
    return typeof candidate.name === 'string' && typeof candidate.storedAt === 'number';
};

const isFresh = (storedAt: number): boolean => Date.now() - storedAt < CARRIED_EVENT_TTL_MS;
